'use client';

import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { tv } from 'tailwind-variants';

import { faChevronDown, faChevronUp } from '@icons/classic/regular';
import { Label } from '../typography';
import { Badge } from './Badge';
import { Checkbox } from './Checkbox';
import { FlexContainer } from './FlexContainer';
import { input } from './Input';
import { Popover, PopoverContent, PopoverTrigger } from './Popover';

import type { FilterOption } from '@bridge/types';
import type { ReactNode } from 'react';

interface MultiSelectCheckboxesProps {
  ariaLabel?: string;
  options: readonly FilterOption[];
  selectedValues: string[];
  onSelectionChange: (values: string[]) => void;
  /** Custom renderer for option labels in the dropdown (e.g., colored badges) */
  renderLabel?: (option: FilterOption) => ReactNode;
  placeholder?: string;
  className?: string;
  maxDisplayCount?: number;
}

const multiSelectTrigger = tv({
  extend: input,
  base: `
    w-fit
    min-w-48
    items-center
    justify-between
    cursor-pointer
    data-[state=open]:ring-1
    data-[state=open]:ring-primary
    data-[state=open]:ring-inset
  `,
});

function MultiSelectCheckboxes({
  ariaLabel,
  options,
  selectedValues,
  onSelectionChange,
  renderLabel,
  placeholder = 'Select',
  className,
  maxDisplayCount = 2,
}: MultiSelectCheckboxesProps) {
  const [isOpen, setIsOpen] = useState(false);

  const selectedSet = new Set(selectedValues);

  function handleToggle(value: string) {
    const next = selectedSet.has(value) ? selectedValues.filter((v) => v !== value) : [...selectedValues, value];
    onSelectionChange(next);
  }

  const selectedOptions = options.filter((o) => selectedSet.has(String(o.value)));

  function getDisplayContent() {
    if (selectedOptions.length === 0) {
      return <span className="text-muted-foreground">{placeholder}</span>;
    }

    const itemsToShow = selectedOptions.slice(0, maxDisplayCount);
    const remainingCount = selectedOptions.length - maxDisplayCount;

    return (
      <FlexContainer className="flex-wrap gap-1" items="center">
        {itemsToShow.map((opt) => (
          <Badge key={String(opt.value)} variant="option">
            {opt.label}
          </Badge>
        ))}
        {remainingCount > 0 && <Badge variant="option">+{remainingCount}</Badge>}
      </FlexContainer>
    );
  }

  return (
    <Popover onOpenChange={setIsOpen} open={isOpen}>
      <PopoverTrigger asChild>
        <button
          className={multiSelectTrigger({ className })}
          aria-label={ariaLabel}
          data-placeholder={selectedOptions.length === 0}
        >
          {getDisplayContent()}
          <FontAwesomeIcon className="size-4 shrink-0 opacity-50" icon={isOpen ? faChevronUp : faChevronDown} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="px-0 py-1.5" align="start">
        {options.map((option) => {
          const value = String(option.value);
          return (
            <FlexContainer className="gap-2 px-3 py-1" items="center" key={value}>
              <Checkbox
                checked={selectedSet.has(value)}
                id={`multiselect-${value}`}
                onCheckedChange={() => handleToggle(value)}
              />
              <Label className="cursor-pointer select-none" htmlFor={`multiselect-${value}`}>
                {renderLabel ? renderLabel(option) : option.label}
              </Label>
            </FlexContainer>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}

export { MultiSelectCheckboxes };
export type { MultiSelectCheckboxesProps };
